import React, { useEffect, useState, useReducer } from 'react';
import SwiperClass from 'swiper';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';

import { LayoutProps } from '../../layouts/layout.props';
import { FormPage } from '../../layouts/slider/page';
import { Header } from '@/components/header';
import { ProgressHeader } from '@/components/ProgressHeader';
import { useSubmission } from '@/components/use.submission';
import SubmitModal from '@/components/SubmitModal';
import {
  ACTIONS,
  SubmissionsState,
  SubmissionsAction,
} from '@/components/layouts/layout.props';
import { SubmissionPublicFieldFragment } from '@/graphql/fragment/submission.public.fragment';
import { EndPage } from './EndPage';
import { Question } from './Question';
import { LoadingPage } from '@/components/loading.page';
import { LOADING_TEXT } from './utils';
import loadingImgUrl from './assets/loading_image.png';
import { StartPage } from './StartPage';
import {StyledProgressHeader} from './InnovationTool.css'
const submissionReducer = (
  submissionList: SubmissionsState[],
  action: SubmissionsAction
) => {
  switch (action.type) {
    case ACTIONS.ADD_SUBMISSION:
      const alreadyExists = submissionList.some(
        (submission) =>
          submission.field === action.payload.field &&
          submission.data === action.payload.data
      );
      if (alreadyExists) return submissionList;
      return [
        ...submissionList.filter(
          (submission) => submission.field !== action.payload.field
        ),
        action.payload,
      ];
    case ACTIONS.DELETE_SUBMISSIONS:
      return [];
    default:
      return submissionList;
  }
};

export const InnovationLayout: React.FC<LayoutProps> = ({ form }) => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass>();
  const { id, design, startPage, endPage, fields, isLive } = form;
  const [submissionCounter, setSubmissionCounter] = useState(0);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const submissionApi = useSubmission(id as string);
  const { startSubmission, finish, setField, fetchSubmissionById } =
    submissionApi;
  const [submissionList, dispatchSubmission] = useReducer(
    submissionReducer,
    []
  );
  const [showProgressBar, setShowProgressBar] = useState(!startPage.show);
  const [progressPercent, setProgressPercent] = useState(0);
  const [lastQuestionIndex, setLastQuestionIndex] = useState(fields.length - 1);
  const [endPageAnswers, setEndPageAnswers] = useState<
    SubmissionPublicFieldFragment[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  const totalVisibleQuestions = fields.filter(
    (field) => field.type !== 'hidden'
  ).length;

  useEffect(() => {
    document.title = form.title;
    for (let i = fields.length - 1; i >= 0; i--) {
      if (fields[i].type !== 'hidden') {
        setLastQuestionIndex(i);
        break;
      }
    }
  }, [fields, form.title]);

  useEffect(() => {
    const handleSubmission = async () => {
      const submission = await startSubmission();
      if (submission) {
        await setField('', submissionList, submission).catch(console.error);
        await finish(submission);
        await new Promise((r) => setTimeout(r, 1000));
        const result = await fetchSubmissionById(submission.id);
        setEndPageAnswers(result.fields);
        setIsLoading(false);
      }
      setProgressPercent(100);
      setShowProgressBar(false);
      setShowSubmitModal(false);
      goToNextSlide();
    };
    if (showSubmitModal) handleSubmission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSubmitModal]);

  useEffect(() => {
    if (submissionCounter !== 0) {
      Array.from(document.getElementsByClassName('MuiInputBase-input')).forEach(
        (textfield) => {
          (textfield as HTMLInputElement).value = '';
        }
      );
    }
  }, [submissionCounter]);

  const goToNextSlide = () => {
    if (!swiperInstance) return;
    swiperInstance.allowSlideNext = true;
    swiperInstance.slideNext();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    swiperInstance.allowSlideNext = false;
  };

  const goToPreviousSlide = () => {
    if (!swiperInstance) return;
    swiperInstance.allowSlidePrev = true;
    swiperInstance.slidePrev();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    swiperInstance.allowSlidePrev = false;
  };

  const goToBeginning = () => {
    if (!swiperInstance) return;
    setProgressPercent(0);
    setShowProgressBar(!startPage.show);
    dispatchSubmission({
      type: ACTIONS.DELETE_SUBMISSIONS,
      payload: { field: '', data: '' },
    });
    setSubmissionCounter((c) => c + 1);
    swiperInstance.allowSlidePrev = true;
    swiperInstance.slideTo(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    swiperInstance.allowSlidePrev = false;
  };

  const swiperSettings: SwiperProps = {
    direction: 'horizontal',
    allowSlideNext: false,
    allowSlidePrev: false,
    preventClicks: false,
    noSwiping: false,
    allowTouchMove: false,
    preventClicksPropagation: false,
    updateOnWindowResize: true,
    keyboard: false,
  };

  const handleFormKeyPress = (event: any) => {
    if (
      event.key === 'Enter' &&
      !event.target.className.includes('MuiInputBase-inputMultiline')
    ) {
      event.preventDefault();
    } else if (
      event.key === ' ' &&
      !event.target.className.includes('MuiInputBase-input')
    ) {
      event.preventDefault();
    }
  };

  // Loading state
  if (isLoading) {
    return <LoadingPage message={LOADING_TEXT} imageUrl={loadingImgUrl} />;
  }

  // End page state
  if (!showSubmitModal && progressPercent === 100 && !isLoading) {
    return (
      <SwiperSlide key='end' style={{ background: design.colors.background }}>
        <>
          <EndPage
            page={endPage}
            design={design}
            answers={endPageAnswers}
          />
        </>
      </SwiperSlide>
    );
  }

  // Main form
  return (
    <>
      {showSubmitModal && <SubmitModal design={design} />}

      <div
        className='swiper-container'
        style={{ background: design.colors.background }}
        onKeyDown={handleFormKeyPress}
      >

        {showProgressBar && (
          <StyledProgressHeader>
          <ProgressHeader
            progressValue={progressPercent}
            steps={fields.length}
            design={design}
            reset={goToBeginning}
          />
          </StyledProgressHeader>
        )}

        <Swiper autoHeight {...swiperSettings} onSwiper={setSwiperInstance}>
          {startPage.show && (
            <SwiperSlide key='start'>
              <StartPage
                page={startPage}
                className='start-page'
                design={design}
                next={goToNextSlide}
                prev={goToBeginning}
                showProgress={setShowProgressBar}
              />
            </SwiperSlide>
          )}

          {fields
            .filter((field) => field.type !== 'hidden')
            .map((field, i) => (
              <SwiperSlide key={field.id} className='slide'>
                <Question
                  field={field}
                  focus={
                    swiperInstance?.activeIndex === (startPage.show ? 1 : 0) + i
                  }
                  design={design}
                  save={(values) => {
                    const fieldObj = {
                      field: field.id,
                      data: JSON.stringify(
                        values[field.id] !== undefined &&
                          values[field.id] !== null
                          ? values[field.id]
                          : '-'
                      ),
                    };
                    dispatchSubmission({
                      type: ACTIONS.ADD_SUBMISSION,
                      payload: fieldObj,
                    });
                    if (i === lastQuestionIndex && isLive) {
                      setShowSubmitModal(true);
                      setIsLoading(true);
                    }
                  }}
                  next={() => {
                    if (i === lastQuestionIndex) {
                      swiperInstance!.allowSlidePrev = false;
                      setShowProgressBar(false);
                    }
                    setProgressPercent(
                      Math.floor(((i + 1) / fields.length) * 100)
                    );
                    goToNextSlide();
                  }}
                  prev={() => {
                    if (i !== 0) {
                      swiperInstance!.allowSlideNext = false;
                      setProgressPercent(
                        Math.floor(((i - 1) / fields.length) * 100)
                      );
                      goToPreviousSlide();
                    }
                  }}
                  slideIndex={i}
                  submissionCount={submissionCounter}
                  lastSlideIndex={lastQuestionIndex}
                  totalQuestions={totalVisibleQuestions}
                />
              </SwiperSlide>
            ))}
        </Swiper>
      </div>
    </>
  );
};
